import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { CheckCircle2, XCircle, Clock, Calendar } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useUser } from "@supabase/auth-helpers-react";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";

interface AttemptWithQuiz {
  id: string;
  quiz_id: string;
  category: string;
  difficulty: string;
  is_correct: boolean;
  points_earned: number;
  time_spent_seconds: number | null;
  created_at: string;
  user_answer: string;
  question?: string;
}

const categoryLabels: Record<string, string> = {
  power_bi: "Power BI",
  sql: "SQL",
  python: "Python",
};

const difficultyColors: Record<string, string> = {
  beginner: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
  intermediate: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
  advanced: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
};

const difficultyLabels: Record<string, string> = {
  beginner: "Iniciante",
  intermediate: "Intermediário",
  advanced: "Avançado",
};

export function QuizHistory() {
  const user = useUser();
  const [attempts, setAttempts] = useState<AttemptWithQuiz[]>([]);
  const [loading, setLoading] = useState(true);
  const [mostMissed, setMostMissed] = useState<{ question: string; count: number }[]>([]);

  useEffect(() => {
    if (user?.id) {
      fetchHistory();
    }
  }, [user?.id]);

  const fetchHistory = async () => {
    if (!user?.id) return;
    
    setLoading(true);
    try {
      // Buscar últimas 20 tentativas com dados do quiz
      const { data: attemptsData, error } = await supabase
        .from("quiz_attempts")
        .select(`
          id,
          quiz_id,
          category,
          difficulty,
          is_correct,
          points_earned,
          time_spent_seconds,
          created_at,
          user_answer
        `)
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(20);

      if (error) throw error;

      // Buscar questões para cada tentativa
      if (attemptsData && attemptsData.length > 0) {
        const quizIds = [...new Set(attemptsData.map(a => a.quiz_id))];
        const { data: quizzes } = await supabase
          .from("quizzes")
          .select("id, question")
          .in("id", quizIds);

        const quizMap = new Map(quizzes?.map(q => [q.id, q.question]) || []);
        
        const enrichedAttempts = attemptsData.map(a => ({
          ...a,
          question: quizMap.get(a.quiz_id) || "Questão não encontrada",
        }));

        setAttempts(enrichedAttempts);

        // Calcular questões mais erradas
        const { data: wrongAttempts } = await supabase
          .from("quiz_attempts")
          .select("quiz_id")
          .eq("user_id", user.id)
          .eq("is_correct", false);

        if (wrongAttempts && wrongAttempts.length > 0) {
          const wrongCounts = wrongAttempts.reduce((acc, a) => {
            acc[a.quiz_id] = (acc[a.quiz_id] || 0) + 1;
            return acc;
          }, {} as Record<string, number>);

          const topWrong = Object.entries(wrongCounts)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 5);

          const wrongQuizIds = topWrong.map(([id]) => id);
          const { data: wrongQuizzes } = await supabase
            .from("quizzes")
            .select("id, question")
            .in("id", wrongQuizIds);

          const wrongQuizMap = new Map(wrongQuizzes?.map(q => [q.id, q.question]) || []);
          
          setMostMissed(
            topWrong.map(([id, count]) => ({
              question: wrongQuizMap.get(id) || "Questão não encontrada",
              count,
            }))
          );
        }
      }
    } catch (error) {
      console.error("Error fetching history:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-40" />
            <Skeleton className="h-4 w-60" />
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-40" />
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (attempts.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">Nenhuma tentativa ainda</h3>
          <p className="text-muted-foreground">
            Comece a responder questões para ver seu histórico aqui.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Últimas Tentativas
          </CardTitle>
          <CardDescription>
            Suas 20 respostas mais recentes
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[400px] pr-4">
            <div className="space-y-3">
              {attempts.map((attempt) => (
                <div
                  key={attempt.id}
                  className={`p-3 rounded-lg border ${
                    attempt.is_correct 
                      ? "border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950" 
                      : "border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {attempt.is_correct ? (
                      <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-600 mt-0.5 shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium line-clamp-2">
                        {attempt.question}
                      </p>
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        <Badge variant="outline" className="text-xs">
                          {categoryLabels[attempt.category] || attempt.category}
                        </Badge>
                        <Badge className={`text-xs ${difficultyColors[attempt.difficulty]}`}>
                          {difficultyLabels[attempt.difficulty] || attempt.difficulty}
                        </Badge>
                        {attempt.is_correct && (
                          <span className="text-xs text-green-600 font-medium">
                            +{attempt.points_earned} pts
                          </span>
                        )}
                        {attempt.time_spent_seconds && (
                          <span className="text-xs text-muted-foreground">
                            {attempt.time_spent_seconds}s
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatDistanceToNow(new Date(attempt.created_at), {
                          addSuffix: true,
                          locale: ptBR,
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <XCircle className="h-5 w-5 text-red-500" />
            Questões para Revisar
          </CardTitle>
          <CardDescription>
            Questões que você mais errou
          </CardDescription>
        </CardHeader>
        <CardContent>
          {mostMissed.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              <CheckCircle2 className="h-10 w-10 mx-auto mb-3 text-green-500" />
              <p>Parabéns! Você não tem erros frequentes.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {mostMissed.map((item, index) => (
                <div
                  key={index}
                  className="p-3 rounded-lg border border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950"
                >
                  <p className="text-sm line-clamp-2">{item.question}</p>
                  <p className="text-xs text-red-600 mt-1">
                    Errada {item.count} {item.count === 1 ? "vez" : "vezes"}
                  </p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
